import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, GitMerge, ShieldAlert, CheckCircle2 } from "lucide-react";

// Read-only duplicate finder for the Ingredient Master with a guarded Merge.
// Reuses the shared /ingredients/duplicates + /ingredients/merge endpoints.
export function DuplicatesDialog({ open, onOpenChange, config, onDone }) {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [pending, setPending] = useState(null); // { masterId, duplicateId, masterName, dupName }
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(config.duplicatesEndpoint)
      .then((res) => setGroups(Array.isArray(res.data) ? res.data : []))
      .catch((e) => toast.error(apiErrorMessage(e, "Could not load duplicates")))
      .finally(() => setLoading(false));
  }, [open, config.duplicatesEndpoint]);

  const doMerge = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      await api.post(config.mergeEndpoint, { masterId: pending.masterId, duplicateId: pending.duplicateId });
      toast.success("Duplicate merged into master. Recipes now point to the master ingredient.");
      setPending(null);
      const res = await api.get(config.duplicatesEndpoint);
      setGroups(Array.isArray(res.data) ? res.data : []);
      onDone && onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Merge failed")); }
    finally { setBusy(false); }
  };

  const itemsOf = (g) => g.items || g.ingredients || g.duplicates || (Array.isArray(g) ? g : []);
  const idOf = (it) => it.id || it.ingredientId;
  const nameOf = (it) => `${it.name}${it.ingredientId || it.code ? ` (${it.ingredientId || it.code})` : ""}`;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-2xl" data-testid="duplicates-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <GitMerge className="h-5 w-5 text-amber-700" /> Ingredient Duplicates
            </DialogTitle>
            <DialogDescription className="text-xs">
              Same-name ingredients on the shared master. Merge keeps one master ID and re-points
              recipes/prices to it. No stock or purchase transaction is affected.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-10 text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Scanning master…</div>
          ) : groups.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-10 text-center text-sm text-muted-foreground" data-testid="duplicates-empty">
              <CheckCircle2 className="h-10 w-10 text-emerald-400" /> No duplicate ingredients found.
            </div>
          ) : (
            <div className="space-y-4">
              {groups.map((g, gi) => {
                const items = itemsOf(g);
                const master = items[0];
                return (
                  <div key={gi} className="rounded-lg border border-stone-200 p-3" data-testid={`duplicate-group-${gi}`}>
                    <div className="mb-2 text-xs font-semibold text-stone-700">{g.name || master?.name || `Group ${gi + 1}`}</div>
                    <div className="space-y-1.5">
                      {items.map((it, ii) => (
                        <div key={ii} className="flex items-center justify-between rounded bg-stone-50 px-2.5 py-1.5 text-sm">
                          <span className="text-stone-800">{nameOf(it)}{ii === 0 && <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">MASTER</span>}</span>
                          {ii > 0 && (
                            <Button data-testid={`duplicate-merge-${gi}-${ii}`} size="sm" variant="outline" className="h-7 gap-1 text-xs"
                              onClick={() => setPending({ masterId: idOf(master), duplicateId: idOf(it), masterName: nameOf(master), dupName: nameOf(it) })}>
                              <GitMerge className="h-3 w-3" /> Merge into master
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-amber-600" /> Merge duplicate ingredient</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{pending?.dupName}</strong> will be merged into <strong>{pending?.masterName}</strong>. Recipes and
              price history that referenced the duplicate will re-point to the master. This affects
              Ingredient Master data only — no stock, GRN or purchase transaction. Proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="merge-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="merge-confirm" className="bg-amber-700 hover:bg-amber-800" disabled={busy} onClick={doMerge}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Yes, merge"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
